import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Layers,
  Sparkles,
  Radar,
  Lock,
  Wrench,
  Headphones,
  Users,
  Building2,
  Code2,
} from 'lucide-react';

const principles = [
  {
    icon: Layers,
    title: 'One operational interface',
    description: 'Reduce context switching between infrastructure tools instead of adding another dashboard on top of them.',
  },
  {
    icon: Sparkles,
    title: 'AI-native workflows',
    description: 'AI is designed into operational workflows rather than bolted on as a separate chatbot.',
  },
  {
    icon: Radar,
    title: 'Infrastructure-aware',
    description: "Recommendations are meant to be grounded in your actual infrastructure state and telemetry, not generic advice.",
  },
  {
    icon: Lock,
    title: 'Human-controlled automation',
    description: 'AI recommends and, where automation exists, acts only within explicit permissions and controls.',
  },
  {
    icon: Wrench,
    title: 'Built for engineering teams',
    description: 'Prioritizing technical workflows, visibility, auditability, and operational safety over consumer-style simplicity.',
  },
];

const audiences = [
  {
    icon: Headphones,
    title: 'DevOps / SRE',
    description: 'Reduce operational context switching and speed up troubleshooting during incidents.',
  },
  {
    icon: Code2,
    title: 'Platform Engineering',
    description: 'Give developers a consistent infrastructure experience without hiding operational controls.',
  },
  {
    icon: Building2,
    title: 'Engineering Leaders',
    description: 'Improve infrastructure visibility and operational efficiency across teams.',
  },
  {
    icon: Users,
    title: 'Developers',
    description: 'Deploy and understand your applications without learning every underlying infrastructure system.',
  },
];

const Benefits: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="why" className="py-32 relative overflow-hidden bg-dark-900">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-cyan/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-semibold mb-6">
            <span className="text-white">Why</span>{' '}
            <span className="gradient-text">DeploAI</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            These are the product principles guiding how DeploAI is being built.
          </p>
        </motion.div>

        {/* Principles */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24"
        >
          {principles.map((principle, index) => (
            <motion.div
              key={principle.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className="glass rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-neon-blue rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <principle.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-neon-blue transition-colors duration-300">
                {principle.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">{principle.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Audience messaging */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl font-semibold text-white mb-4">Built for every role on the team</h3>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {audiences.map((audience) => (
            <div key={audience.title} className="feature-card text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-neon-blue rounded-xl flex items-center justify-center mx-auto mb-4">
                <audience.icon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">{audience.title}</h4>
              <p className="text-sm text-gray-400">{audience.description}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;
